import { useMainLayout } from '../../../layouts/MainPageLayout/MainLayoutContext';
import { DisplayingRegionLevel } from '../../../../types/DisplayingRegionLevel';
import type { PortfolioAnalyticsPropertyResponseDto } from '@zg-rentals/ts-customer-analytics-client';
import { useState } from 'react';
import { isClaimedProperty } from '../../../../utils/isClaimedProperty';
import type { MapDotPayload } from '../../../SharedComponents/Map/Dots/Dots';
import { format } from 'date-fns';
import { localDateToDate } from '../../../../utils/localDateToDate';
import { useSidebarTitle } from '../../../../hooks/useSidebarTitle';

export const displayingModesWithPropertyMarkers = [
  DisplayingRegionLevel.METRO_AREA,
  DisplayingRegionLevel.CITY,
  DisplayingRegionLevel.ZIP,
];

const getPropertyDotPayload = (
  property: PortfolioAnalyticsPropertyResponseDto,
): { key: string; payload: MapDotPayload } => {
  const alias = property.propertyDetails.listingAlias;

  if (isClaimedProperty(property)) {
    return { key: `${alias}-claimed`, payload: { type: 'claimedProperty', data: property } };
  }

  return { key: alias, payload: { type: 'property', data: property } };
};

export interface ProspectOpportunity {
  propertyName: string;
  propertyAlias: string;
  prospectCount: number;
}

export const useOverviewMapData = () => {
  const { filteredProperties, selectedRegionId, selectedDisplayingRegionLevel, visibleRegionIds, displayMode } =
    useMainLayout();
  const [showRegularProperties, setShowRegularProperties] = useState(true);
  const [showClaimedProperties, setShowClaimedProperties] = useState(true);
  const propertiesToRenderMap = new Map<string, PortfolioAnalyticsPropertyResponseDto>();

  let propertiesToFilter = filteredProperties;

  if (!showRegularProperties) {
    propertiesToFilter = propertiesToFilter.filter((property) => isClaimedProperty(property));
  }

  if (!showClaimedProperties) {
    propertiesToFilter = propertiesToFilter.filter((property) => !isClaimedProperty(property));
  }

  const propertiesInVisibleRegion = filteredProperties.filter((property) =>
    visibleRegionIds.some(
      (region) =>
        region.regionId === property.propertyDetails.stateRegionId ||
        region.regionId === property.propertyDetails.metroStatRegionId ||
        region.regionId === property.propertyDetails.cityRegionId ||
        region.regionId === property.propertyDetails.zipRegionId,
    ),
  );

  const advertisedProperties = propertiesInVisibleRegion.filter((property) => !isClaimedProperty(property));
  const claimedProperties = propertiesInVisibleRegion.filter((property) => isClaimedProperty(property));

  const currentDateFormatted = format(new Date(), 'MMM yyyy');

  const currentMonthTopStats = Object.values(propertiesInVisibleRegion)
    .filter((property) => property.currentPublishedStatus === 'Published')
    .flatMap((property) =>
      property.breakdownStats
        .filter((stat) => {
          const statDate = localDateToDate(stat.statDate);
          const formattedDate = format(statDate, 'MMM yyyy');
          return formattedDate === currentDateFormatted;
        })
        .map((stat) => ({
          propertyAlias: property.propertyDetails.listingAlias,
          propertyName: property.propertyDetails.listingName,
          stat,
          totalProspects: stat.leadCount + stat.listingWebReferrals,
        })),
    )
    .sort((a, b) => b.totalProspects - a.totalProspects);

  const topPropertiesByProspects: Array<ProspectOpportunity> =
    currentMonthTopStats.length >= 10
      ? currentMonthTopStats.slice(0, 5).map((item) => ({
          propertyAlias: item.propertyAlias,
          propertyName: item.propertyName,
          prospectCount: item.totalProspects,
        }))
      : [];

  const bottomPropertiesByProspects: Array<ProspectOpportunity> =
    currentMonthTopStats.length >= 10
      ? currentMonthTopStats.slice(-5).map((item) => ({
          propertyAlias: item.propertyAlias,
          propertyName: item.propertyName,
          prospectCount: item.totalProspects,
        }))
      : [];

  if (selectedRegionId && selectedDisplayingRegionLevel !== DisplayingRegionLevel.STATE) {
    for (const property of propertiesToFilter) {
      const propertyKey = `${property.propertyDetails.lat}-${property.propertyDetails.lon}`;

      if (
        (property.propertyDetails.cityRegionId === Number(selectedRegionId) ||
          property.propertyDetails.metroStatRegionId === Number(selectedRegionId) ||
          property.propertyDetails.zipRegionId === Number(selectedRegionId)) &&
        !propertiesToRenderMap
          .values()
          .some((p) => p.propertyDetails.listingAlias === property.propertyDetails.listingAlias) &&
        !propertiesToRenderMap.has(propertyKey)
      ) {
        propertiesToRenderMap.set(propertyKey, property);
      }
    }
  } else if (displayingModesWithPropertyMarkers.includes(displayMode)) {
    visibleRegionIds.forEach(({ regionId }) => {
      for (const property of propertiesToFilter) {
        const propertyKey = `${property.propertyDetails.lat}-${property.propertyDetails.lon}`;

        if (
          (property.propertyDetails.cityRegionId === Number(regionId) ||
            property.propertyDetails.metroStatRegionId === Number(regionId) ||
            property.propertyDetails.zipRegionId === Number(regionId)) &&
          !propertiesToRenderMap
            .values()
            .some((p) => p.propertyDetails.listingAlias === property.propertyDetails.listingAlias) &&
          !propertiesToRenderMap.has(propertyKey)
        ) {
          propertiesToRenderMap.set(propertyKey, property);
        }
      }
    });
  }

  const propertiesToRender = Array.from(propertiesToRenderMap.values());
  const sidebarTitle = useSidebarTitle();

  const dotsToRender = propertiesToRender.map((property) => getPropertyDotPayload(property));

  return {
    showRegularProperties,
    showClaimedProperties,
    setShowRegularProperties,
    setShowClaimedProperties,
    advertisedProperties,
    claimedProperties,
    dotsToRender,
    topPropertiesByProspects,
    bottomPropertiesByProspects,
    sidebarTitle,
  };
};
